import React, { useState, useEffect } from "react";
import { Phone, Send, MessageSquare, RefreshCw, XCircle, PanelRightClose } from "lucide-react";
import Button from "../Button/Button.js";
import "./ChatSimulator.css";

export default function ChatSimulator({
  selectedAgent,
  chatMessages = [],
  isTyping,
  chatInput,
  setChatInput,
  handleSendChatMessage,
  handleOpenChatSimulator,
  handleOpenDialerModal,
  chatBottomRef,
  simWidth,
  connectionStatus = "disconnected",
  onEndChat,
  onReconnectChat,
  onCloseSimulator
}) {
  const [chatStarted, setChatStarted] = useState(false);

  const isUnsavedDraft = !selectedAgent?.id || String(selectedAgent.id).startsWith("mock-") || String(selectedAgent.id).startsWith("new-agent-");

  useEffect(() => {
    setChatStarted(false);
  }, [selectedAgent?.id]);

  const handleStartChat = async () => {
    if (isUnsavedDraft) return;
    if (handleOpenChatSimulator) {
      const ok = await handleOpenChatSimulator();
      if (ok === false) return;
    }
    setChatStarted(true);
  };

  const handleStopChat = () => {
    setChatStarted(false);
    if (onEndChat) onEndChat();
  };

  return (
    <section className="studio-simulator-panel" style={{ width: simWidth }}>
      {/* Messaging Sandbox Playground */}
      <div className="playground-container">
        <div className="playground-header">
          <div className="sim-title-group">
            <span className="playground-title">Chat with Agent</span>
            {chatStarted && (
              <span className={`sim-ws-badge ${connectionStatus}`}>
                <span className="sim-ws-dot" />
                <span className="sim-ws-label">
                  {connectionStatus === "connected"
                    ? "Connected"
                    : connectionStatus === "connecting"
                    ? "Connecting..."
                    : "Disconnected"}
                </span>
              </span>
            )}
          </div>

          <div className="sim-header-actions">
            {chatStarted && (connectionStatus === "disconnected" || connectionStatus === "error") && (
              <Button
                variant="outline"
                size="sm"
                className="sim-action-btn"
                onClick={onReconnectChat}
                title="Reconnect WebSocket"
              >
                <RefreshCw size={12} /> <span className="btn-text">Reconnect</span>
              </Button>
            )}

            {chatStarted && (
              <Button
                variant="outline"
                size="sm"
                className="sim-action-btn"
                onClick={handleStopChat}
                title="End Chat Session"
              >
                <XCircle size={12} /> <span className="btn-text">End</span>
              </Button>
            )}

            {/* <Button
              variant="outline"
              size="sm"
              className="sim-action-btn btn-test-call"
              onClick={handleOpenDialerModal}
              disabled={isUnsavedDraft}
              title={isUnsavedDraft ? "Please save the agent first to enable test calls" : "Test Call"}
            >
              <Phone size={12} /> <span className="btn-text">Test Call</span>
            </Button> */}

            {onCloseSimulator && (
              <Button
                variant="ghost"
                size="sm"
                className="sim-close-btn"
                onClick={onCloseSimulator}
                title="Collapse Panel"
                style={{ padding: "4px 6px", color: "#64748b", marginLeft: "2px" }}
              >
                <PanelRightClose size={16} />
              </Button>
            )}
          </div>
        </div>

        {!chatStarted ? (
          <div className="sim-empty-state">
            <div className={`sim-empty-icon-wrap ${isUnsavedDraft ? "draft" : "active"}`}>
              <MessageSquare size={26} />
            </div>
            <h4 className="sim-empty-title">Test Agent Conversation</h4>
            <p className="sim-empty-desc">
              {isUnsavedDraft 
                ? "Please save the agent first to enable the Chat Simulator and Test Calls."
                : `Click below to start an interactive chat session with ${selectedAgent?.campaign_name || "this agent"}.`}
            </p>
            <Button
              variant="primary"
              size="md"
              onClick={handleStartChat}
              disabled={isUnsavedDraft}
              title={isUnsavedDraft ? "Please save the agent first to start chat" : "Chat with Agent"}
              className="sim-start-btn"
            >
              <MessageSquare size={16} /> Chat with Agent
            </Button>
          </div>
        ) : (
          <>
            <div className="playground-chat-area">
              {chatMessages.map((msg) => (
                <div key={msg.id} className={`chat-bubble ${msg.sender}`}>
                  <div>{msg.text}</div>
                  {msg.latency > 0 && (
                    <div className="latency-indicator">Latency: {msg.latency}ms</div>
                  )}
                </div>
              ))}
              {isTyping && (
                <div className="chat-typing-dots">
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </div>
              )}
              <div ref={chatBottomRef} />
            </div>

            <form className="playground-input-box" onSubmit={handleSendChatMessage}>
              <input
                type="text"
                placeholder="Type message to test response flow..."
                className="chat-text-input"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                disabled={connectionStatus !== "connected"}
              />
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="btn-send-chat"
                title="Send test message"
                disabled={connectionStatus !== "connected" || !chatInput.trim()}
              >
                <Send size={16} />
              </Button>
            </form>

            <div className="playground-instruction-banner">
              {connectionStatus === "connected"
                ? "Live WebSocket connection active. Prompting agent using saved configuration."
                : "Connecting to agent WebSocket..."}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
